import type { StringKey } from '@/i18n';
import { DOMAIN_SPECS } from '@/features/screening/domains';
import type { HrsnDomain } from '@/types/api';
import type {
  AttestationStatus,
  ClosedReasonCode,
  DashboardFilter,
  FormLedgerStatus,
  NavActivity,
  NavAttemptOutcome,
  NavAttemptType,
  NavFormType,
  NavNoteType,
  NavStatus,
  NextActionKey,
  NotReferredReason,
  PreauthStatus,
} from '@/features/navigator/navigator.api';

/**
 * Enum → i18n key. Typed as StringKey so a key that does not exist fails the
 * build instead of rendering its own name at the member's bedside.
 */
export const ATTEMPT_TYPE_KEYS: Record<NavAttemptType, StringKey> = {
  call: 'navigator.attemptType.call',
  text: 'navigator.attemptType.text',
  email: 'navigator.attemptType.email',
  other: 'navigator.attemptType.other',
};

export const OUTCOME_KEYS: Record<NavAttemptOutcome, StringKey> = {
  no_answer: 'navigator.outcome.no_answer',
  voicemail: 'navigator.outcome.voicemail',
  wrong_number: 'navigator.outcome.wrong_number',
  reached: 'navigator.outcome.reached',
  refused: 'navigator.outcome.refused',
  callback_scheduled: 'navigator.outcome.callback_scheduled',
  deceased_reported: 'navigator.outcome.deceased_reported',
  no_show: 'navigator.outcome.no_show',
  other: 'navigator.outcome.other',
};

export const ACTIVITY_KEYS: Record<NavActivity, StringKey> = {
  eligibility_assessment: 'navigator.activity.eligibility_assessment',
  care_management: 'navigator.activity.care_management',
  follow_up: 'navigator.activity.follow_up',
  referral_coordination: 'navigator.activity.referral_coordination',
};

export const STATUS_KEYS: Record<NavStatus, StringKey> = {
  suggested: 'navigator.status.suggested',
  flagged: 'navigator.status.flagged',
  in_progress: 'navigator.status.in_progress',
  completed: 'navigator.status.completed',
  declined_by_patient: 'navigator.status.declined_by_patient',
  not_required: 'navigator.status.not_required',
  // Causa externa (migracion 056): se mudo, perdio la cobertura, no se lo pudo
  // ubicar, fallecio. Antes los cuatro decian "no hacia falta navegar".
  closed_external: 'navigator.status.closed_external',
};

/**
 * Steps 1-7 are the CEO's own words; step 8 was added so the mirroring into
 * findhelp is a step someone owns rather than something everyone assumes.
 */
export const CHECKLIST_STEP_KEYS: Record<number, StringKey> = {
  1: 'navigator.checklist.step1',
  2: 'navigator.checklist.step2',
  3: 'navigator.checklist.step3',
  4: 'navigator.checklist.step4',
  5: 'navigator.checklist.step5',
  6: 'navigator.checklist.step6',
  7: 'navigator.checklist.step7',
  8: 'navigator.checklist.step8',
};

/** findhelp history state -> label. Typed, so a bad key fails the build. */
export const FINDHELP_HISTORY_KEYS: Record<string, StringKey | undefined> = {
  none: 'adminNav.fh.none',
  incomplete: 'adminNav.fh.incomplete',
  complete: 'adminNav.fh.complete',
};

/** Audit actions that describe the case itself; anything else is not shown. */
export const LIFECYCLE_KEYS: Record<string, StringKey | undefined> = {
  NAV_CASE_CREATED_AUTO: 'navigator.lifecycle.created',
  NAV_CASE_CLAIMED: 'navigator.lifecycle.claimed',
  NAV_CASE_STATUS_CHANGED: 'navigator.lifecycle.statusChanged',
  NAV_CASE_OVERRIDE: 'navigator.lifecycle.override',
};

export const NOTE_TYPE_KEYS: Record<NavNoteType, StringKey> = {
  general: 'navigator.noteType.general',
  ea: 'navigator.noteType.ea',
  outreach: 'navigator.noteType.outreach',
  referral: 'navigator.noteType.referral',
  follow_up: 'navigator.noteType.follow_up',
};

/**
 * Domain names come from the screening spec that already exists — the agent and
 * the navigator should be reading the same words for the same domain.
 */
export function domainLabel(domain: string, lang: 'en' | 'es'): string {
  const spec = DOMAIN_SPECS[domain as HrsnDomain];
  if (!spec) return domain;
  return lang === 'es' ? spec.titleEs : spec.title;
}

// ---------------------------------------------------------------- Wave 11

export const ATTESTATION_STATUS_KEYS: Record<AttestationStatus, StringKey> = {
  no_requerida: 'navigator.docs.att.no_requerida',
  pendiente: 'navigator.docs.att.pendiente',
  enviada: 'navigator.docs.att.enviada',
  recibida: 'navigator.docs.att.recibida',
  rechazada: 'navigator.docs.att.rechazada',
};

export const PREAUTH_STATUS_KEYS: Record<PreauthStatus, StringKey> = {
  no_requerida: 'navigator.docs.pre.no_requerida',
  pendiente: 'navigator.docs.pre.pendiente',
  en_revision: 'navigator.docs.pre.en_revision',
  autorizada: 'navigator.docs.pre.autorizada',
  denegada: 'navigator.docs.pre.denegada',
};

/** Las siete razones de cierre catalogadas (CEO 2026-08-09, migración 055). */
export const CLOSED_REASON_KEYS: Record<ClosedReasonCode, StringKey> = {
  completado: 'navigator.closeReason.completado',
  completado_pre_modulo: 'navigator.closeReason.completado_pre_modulo',
  no_consent_ea: 'navigator.closeReason.no_consent_ea',
  withdrew_after_consent: 'navigator.closeReason.withdrew_after_consent',
  unreachable_3_attempts: 'navigator.closeReason.unreachable_3_attempts',
  lost_medicaid: 'navigator.closeReason.lost_medicaid',
  moved_out_of_region: 'navigator.closeReason.moved_out_of_region',
  deceased: 'navigator.closeReason.deceased',
  services_already_in_place: 'navigator.closeReason.services_already_in_place',
  // E-19: negativo sin necesidades — etiqueta honesta, jamás «no consintió».
  negative_no_needs: 'navigator.closeReason.negative_no_needs',
  // Server-only (prior-navigation verify): nunca en el selector, siempre nombrable.
  prior_navigation_verified: 'navigator.closeReason.prior_navigation_verified',
};

/**
 * Cómo se llama un servicio en pantalla.
 *
 * Los dominios reusan el vocabulario del screening — el agente y el navigator
 * tienen que leer las mismas palabras. Las dos categorías con preautorización
 * obligatoria llevan el nombre que les da el manual (§VIII y §IX). Cualquier
 * otra clave se muestra tal cual: no se inventa una traducción para algo que no
 * tiene fuente.
 */
const SERVICE_KEY_LABELS: Record<string, { es: string; en: string }> = {
  home_mods_remediation: {
    es: 'Modificaciones y remediación del hogar',
    en: 'Home Accessibility & Safety Modifications',
  },
  utility_setup_assistance: {
    es: 'Asistencia con servicios básicos',
    en: 'Utility Setup Assistance',
  },
};

export function serviceLabel(serviceKey: string, lang: 'en' | 'es'): string {
  const named = SERVICE_KEY_LABELS[serviceKey];
  if (named) return lang === 'es' ? named.es : named.en;
  return domainLabel(serviceKey, lang);
}

/**
 * `YYYY-MM-DD` as a LOCAL day. `new Date('2026-08-04')` parses as UTC midnight,
 * which in New York is the evening of the 3rd — the same off-by-one that gave
 * every case opened after 8pm an extra day of SLA.
 */
export function daysUntil(isoDate: string | null): number | null {
  if (!isoDate) return null;
  const [y, m, d] = isoDate.split('-').map(Number);
  if (!y || !m || !d) return null;
  const target = new Date(y, m - 1, d);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target.getTime() - startOfToday.getTime()) / 86_400_000);
}

export function initials(first: string, last: string): string {
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || '··';
}

/** What a stretch of measured work was spent on (Wave 6D). */
export const TIMER_ACTIVITIES = [
  'precall', 'ea', 'espejo_findhelp', 'documentacion', 'otro',
] as const;

export type TimerActivityKey = (typeof TIMER_ACTIVITIES)[number];

export const TIMER_ACTIVITY_KEYS: Record<TimerActivityKey, StringKey> = {
  precall: 'navigator.timer.act.precall',
  ea: 'navigator.timer.act.ea',
  espejo_findhelp: 'navigator.timer.act.espejo',
  documentacion: 'navigator.timer.act.documentacion',
  otro: 'navigator.timer.act.otro',
};

/**
 * Por qué NO se refiere un servicio (REORG-3).
 *
 * Tipado por la unión real y no por `string`, para que un valor nuevo del
 * backend rompa el build acá en vez de renderizar su propia clave delante del
 * miembro.
 */
export const NOT_REFERRED_REASON_KEYS: Record<NotReferredReason, StringKey> = {
  ya_lo_recibe: 'navigator.notReferred.ya_lo_recibe',
  no_consintio: 'navigator.notReferred.no_consintio',
  miembro_se_retiro: 'navigator.notReferred.miembro_se_retiro',
  fuera_de_cobertura: 'navigator.notReferred.fuera_de_cobertura',
  otro: 'navigator.notReferred.otro',
};

export const NEXT_ACTION_KEYS: Record<NextActionKey, StringKey> = {
  call_or_schedule: 'navigator.workspace.action.call_or_schedule',
  verify_prior_navigation: 'navigator.workspace.action.verify_prior_navigation',
  complete_ea: 'navigator.workspace.action.complete_ea',
  register_ea: 'navigator.workspace.action.register_ea',
  review_needs: 'navigator.workspace.action.review_needs',
  record_goal: 'navigator.workspace.action.record_goal',
  confirm_referral_consent: 'navigator.workspace.action.confirm_referral_consent',
  record_referral: 'navigator.workspace.action.record_referral',
  notify_member: 'navigator.workspace.action.notify_member',
  close_session: 'navigator.workspace.action.close_session',
  complete_followup: 'navigator.workspace.action.complete_followup',
  continue_service_followup: 'navigator.workspace.action.continue_service_followup',
  close_episode: 'navigator.workspace.action.close_episode',
  attempts_exhausted: 'navigator.workspace.action.attempts_exhausted',
  closed: 'navigator.workspace.action.closed',
};

export function nextActionLabel(key: string): StringKey {
  return NEXT_ACTION_KEYS[key as NextActionKey] ?? 'navigator.workspace.action.continue_service_followup';
}

export const FORM_TYPE_KEYS: Record<NavFormType, StringKey> = {
  hrsn_screener: 'navigator.workspace.form.hrsn_screener',
  eligibility_assessment: 'navigator.workspace.form.eligibility_assessment',
  outreach_tracker: 'navigator.workspace.form.outreach_tracker',
  navigation_reimbursement: 'navigator.workspace.form.navigation_reimbursement',
  no_consent_findhelp: 'navigator.workspace.form.no_consent_findhelp',
  negative_closed_findhelp: 'navigator.workspace.form.negative_closed_findhelp',
};

export const FORM_STATUS_KEYS: Record<FormLedgerStatus, StringKey> = {
  draft: 'navigator.workspace.formStatus.draft',
  submitted: 'navigator.workspace.formStatus.submitted',
  verified: 'navigator.workspace.formStatus.verified',
  pending_evidence: 'navigator.workspace.formStatus.pending_evidence',
};

/**
 * D-05/C-7: la regla que manda cada follow-up, citada en pantalla. Vivía
 * privada en WorkspaceAgenda; acá la comparten Agenda y Timeline. Incluye
 * post_initial_reimbursement_10d — justo la regla que crea la primera unidad.
 */
export const RULE_KEY_LABELS: Record<string, StringKey | undefined> = {
  post_initial_reimbursement_10d: 'navigator.followup.rule.post_initial_reimbursement_10d',
  post_completion_5bd: 'navigator.followup.rule.post_completion_5bd',
  pre_completion_60d: 'navigator.followup.rule.pre_completion_60d',
  pre_completion_10d: 'navigator.followup.rule.pre_completion_10d',
  pre_completion_14d: 'navigator.followup.rule.pre_completion_14d',
  referral_no_response: 'navigator.followup.rule.referral_no_response',
  no_show_reschedule: 'navigator.followup.rule.no_show_reschedule',
};

export function ruleKeyLabel(ruleKey: string): StringKey {
  return RULE_KEY_LABELS[ruleKey] ?? 'navigator.followup.rule.generic';
}

/** D-08: estado de una cita, traducido — no el enum crudo de bookings. */
export const APPT_STATUS_KEYS: Record<string, StringKey | undefined> = {
  scheduled: 'navigator.appt.status.scheduled',
  rescheduled: 'navigator.appt.status.rescheduled',
  cancelled: 'navigator.appt.status.cancelled',
  completed: 'navigator.appt.status.completed',
  missed: 'navigator.appt.status.missed',
  no_show: 'navigator.appt.status.no_show',
};

/** D-07: los 9 estados reales de un track (tracks.service SERVICE_STATUSES). */
export const SVC_STATUS_KEYS: Record<string, StringKey | undefined> = {
  identified: 'navigator.svcStatus.identified',
  goal_pending: 'navigator.svcStatus.goal_pending',
  referral_pending: 'navigator.svcStatus.referral_pending',
  provider_pending: 'navigator.svcStatus.provider_pending',
  service_pending: 'navigator.svcStatus.service_pending',
  service_active: 'navigator.svcStatus.service_active',
  followup_due: 'navigator.svcStatus.followup_due',
  terminal: 'navigator.workspace.terminal',
  blocked: 'navigator.svcStatus.blocked',
};

/** C-3: los 11 resultados reales de un servicio (tracks.service SERVICE_RESULTS). */
export const SVC_RESULT_KEYS: Record<string, StringKey | undefined> = {
  received: 'navigator.svcResult.received',
  active: 'navigator.svcResult.active',
  need_resolved: 'navigator.svcResult.need_resolved',
  member_declined: 'navigator.svcResult.member_declined',
  provider_denied: 'navigator.svcResult.provider_denied',
  provider_no_response: 'navigator.svcResult.provider_no_response',
  not_eligible: 'navigator.svcResult.not_eligible',
  duplicate_service: 'navigator.svcResult.duplicate_service',
  moved_out_of_region: 'navigator.svcResult.moved_out_of_region',
  deceased: 'navigator.svcResult.deceased',
  other: 'navigator.svcResult.other',
};

/** D-10: por qué terminó un tramo del reloj — traducido, no el código crudo. */
export const END_REASON_KEYS: Record<string, StringKey | undefined> = {
  manual: 'navigator.endReason.manual',
  switch: 'navigator.endReason.switch',
  route_exit: 'navigator.endReason.route_exit',
  idle: 'navigator.endReason.idle',
  logout: 'navigator.endReason.logout',
  max_duration: 'navigator.endReason.max_duration',
  reassigned: 'navigator.endReason.reassigned',
  case_closed: 'navigator.endReason.case_closed',
  owner_lost: 'navigator.endReason.owner_lost',
};

const NY = 'America/New_York';

/** Dates that billing and SLA care about are New York civil days, never the browser's zone. */
export function fmtNy(iso: string | null | undefined, kind: 'date' | 'datetime' = 'datetime'): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return kind === 'date'
    ? d.toLocaleDateString(undefined, { timeZone: NY, year: 'numeric', month: 'short', day: 'numeric' })
    : d.toLocaleString(undefined, { timeZone: NY, dateStyle: 'medium', timeStyle: 'short' });
}

export function asStringList(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String).map((s) => s.trim()).filter(Boolean);
  if (typeof value === 'string' && value.length > 0) {
    return value.replace(/[{}]/g, '').split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

/** mm:ss, or h:mm:ss past the hour. */
export function hhmmss(total: number): string {
  const s = Math.max(0, Math.floor(total));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
}

export const DASH_CARD_KEYS: Record<DashboardFilter, StringKey> = {
  cases: 'navigator.dash.card.cases',
  in_progress_today: 'navigator.dash.card.in_progress_today',
  appointments_today: 'navigator.dash.card.appointments_today',
  contacts_pending: 'navigator.dash.card.contacts_pending',
  followups_due: 'navigator.dash.card.followups_due',
  no_shows: 'navigator.dash.card.no_shows',
  ready_to_close: 'navigator.dash.card.ready_to_close',
  attempts_exhausted: 'navigator.dash.card.attempts_exhausted',
  waiting_provider: 'navigator.dash.card.waiting_provider',
};

// ---------------------------------------------------- Design spec 2026-08-19

/** §08 — canal del aviso del referido. */
export const NOTIFY_CHANNEL_KEYS: Record<string, StringKey | undefined> = {
  sms: 'navigator.notify.channel.sms',
  email: 'navigator.notify.channel.email',
  told_in_call: 'navigator.notify.channel.told_in_call',
};

/** §08 — motivo del escalamiento a supervisión. */
export const ESCALATION_REASON_KEYS: Record<string, StringKey | undefined> = {
  units_exhausted: 'navigator.escalate.reason.units_exhausted',
  provider_no_response: 'navigator.escalate.reason.provider_no_response',
  other: 'navigator.escalate.reason.other',
};

/** §04 — las 4 respuestas de «¿alguien ya la ayudó?». */
export const PRIOR_HELP_ANSWER_KEYS: Record<string, StringKey | undefined> = {
  none: 'navigator.priorHelp.none',
  partial: 'navigator.priorHelp.partial',
  provider_working: 'navigator.priorHelp.provider_working',
  full: 'navigator.priorHelp.full',
};

export function formatNy(
  iso: string | null | undefined,
  lang: 'en' | 'es',
  opts?: Intl.DateTimeFormatOptions,
): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return String(iso);
  return new Intl.DateTimeFormat(lang === 'es' ? 'es-US' : 'en-US', {
    timeZone: NY,
    ...opts,
  }).format(date);
}

export function formatNyDateTime(iso: string | null | undefined, lang: 'en' | 'es'): string {
  return formatNy(iso, lang, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

